import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { ShoppingCart, ShoppingBag, Minus, Plus, X, Loader2, Truck, ArrowRight, Lock, ShieldCheck } from "lucide-react"
import { useCartStore } from "@/stores/cartStore"
import { create } from "zustand"
import { Link } from "react-router-dom"

const FREE_SHIPPING_THRESHOLD = 45

interface CartDrawerStore {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  openCart: () => void
}

export const useCartDrawerStore = create<CartDrawerStore>((set) => ({
  isOpen: false,
  setIsOpen: (open) => set({ isOpen: open }),
  openCart: () => set({ isOpen: true }),
}))

export const CartDrawer = () => {
  const { isOpen, setIsOpen } = useCartDrawerStore()

  const items = useCartStore((state) => state.items)
  const isLoading = useCartStore((state) => state.isLoading)
  const lastAddedVariantId = useCartStore((state) => state.lastAddedVariantId)
  const updateQuantity = useCartStore((state) => state.updateQuantity)
  const removeItem = useCartStore((state) => state.removeItem)
  const createCheckout = useCartStore((state) => state.createCheckout)

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
  const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0)
  const remaining = Math.max(FREE_SHIPPING_THRESHOLD - totalPrice, 0)
  const shippingProgress = Math.min((totalPrice / FREE_SHIPPING_THRESHOLD) * 100, 100)
  const hasFreeShipping = remaining <= 0

  const handleCheckout = async () => {
    try {
      const checkoutUrl = await createCheckout()
      if (checkoutUrl) {
        window.location.href = checkoutUrl
      }
    } catch (error) {
      console.error("Checkout failed:", error)
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          className="relative flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={totalItems > 0 ? `Košarica (${totalItems})` : "Košarica"}
        >
          <ShoppingCart className="h-5 w-5" />
          {totalItems > 0 && (
            <span
              key={totalItems}
              className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground animate-hero-scale-in"
            >
              {totalItems}
            </span>
          )}
        </button>
      </SheetTrigger>

      <SheetContent className="w-full sm:max-w-md flex flex-col h-full p-0">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-display text-xl font-bold">
              Košarica
              {totalItems > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({totalItems})
                </span>
              )}
            </SheetTitle>
          </div>
        </SheetHeader>

        <div className="flex flex-col flex-1 min-h-0">
          {items.length === 0 ? (
            /* Empty state */
            <div className="flex-1 flex items-center justify-center px-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-5">
                  <ShoppingBag className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="font-display font-semibold text-foreground mb-1">Košarica je prazna</p>
                <p className="text-sm text-muted-foreground mb-6">Dodajte magnetne ploščice in začnite graditi</p>
                <Button asChild className="rounded-xl px-6" onClick={() => setIsOpen(false)}>
                  <Link to="/trgovina">
                    Pojdi v trgovino
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Free shipping progress */}
              <div className="flex-shrink-0 mx-6 mt-4 mb-2">
                <div className={`rounded-xl p-3 ${hasFreeShipping ? "bg-primary/5" : "bg-muted/50"}`}>
                  {hasFreeShipping ? (
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Truck className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-sm font-semibold text-primary">Imate brezplačno dostavo!</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <Truck className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          Do brezplačne dostave manjka še <span className="font-semibold text-foreground">{remaining.toFixed(2)} €</span>
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-border overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
                          style={{ width: `${shippingProgress}%` }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Scrollable items */}
              <div className="flex-1 overflow-y-auto px-6 py-3 min-h-0">
                <div className="space-y-3">
                  {items.map((item) => {
                    const lineTotal = parseFloat(item.price.amount) * item.quantity
                    const isHighlighted = lastAddedVariantId === item.variantId

                    return (
                        <div
                          key={item.variantId}
                          className={`relative flex gap-3 rounded-2xl p-3 animate-hero-fade-up transition-colors duration-500 ${
                            isHighlighted
                              ? "bg-primary/8 ring-1 ring-primary/15"
                              : "bg-card border border-border/50"
                          }`}
                        >
                          {/* Product image */}
                          <div className="w-20 h-20 md:w-[72px] md:h-[72px] bg-white rounded-xl overflow-hidden flex-shrink-0 border border-border/30">
                            {item.product.node.images?.edges?.[0]?.node ? (
                              <img
                                src={`${item.product.node.images.edges[0].node.url}${item.product.node.images.edges[0].node.url.includes("?") ? "&" : "?"}width=160&format=webp`}
                                alt={item.product.node.title}
                                className="w-full h-full object-contain p-1"
                                width={80}
                                height={80}
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                              </div>
                            )}
                          </div>

                          {/* Product info */}
                          <div className="flex-1 min-w-0 flex flex-col justify-between">
                            <div>
                              <div className="flex items-start justify-between gap-2">
                                <h4 className="font-medium text-sm leading-tight line-clamp-2 pr-1">
                                  {item.product.node.title}
                                </h4>
                                <button
                                  className="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 active:bg-destructive/10 transition-colors flex-shrink-0 touch-manipulation"
                                  onClick={() => removeItem(item.variantId)}
                                  aria-label="Odstrani izdelek"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                              {item.variantTitle !== "Default Title" && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {item.variantTitle}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center justify-between mt-2">
                              {/* Quantity controls */}
                              <div className="flex items-center gap-0 rounded-lg border border-border overflow-hidden">
                                <button
                                  className="h-10 w-10 md:h-8 md:w-8 flex items-center justify-center text-muted-foreground hover:bg-muted active:bg-muted transition-colors touch-manipulation"
                                  onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                                  aria-label="Zmanjšaj količino"
                                >
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                                <span className="w-8 text-center text-sm md:text-xs font-semibold text-foreground">
                                  {item.quantity}
                                </span>
                                <button
                                  className="h-10 w-10 md:h-8 md:w-8 flex items-center justify-center text-muted-foreground hover:bg-muted active:bg-muted transition-colors touch-manipulation"
                                  onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                                  aria-label="Povečaj količino"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>

                              {/* Line total */}
                              <p className="text-base md:text-sm font-bold text-foreground">
                                {lineTotal.toFixed(2)} €
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </div>
              </div>

              {/* Checkout footer */}
              <div
                className="flex-shrink-0 border-t border-border bg-card/50 px-6 py-5"
                style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
              >
                {/* Subtotal */}
                <div className="flex justify-between items-center mb-4">
                  <span className="text-muted-foreground">Vmesni seštevek</span>
                  <span className="text-xl font-bold font-display text-foreground">
                    {totalPrice.toFixed(2)} €
                  </span>
                </div>

                {/* Checkout button */}
                <Button
                  onClick={handleCheckout}
                  className="w-full h-12 text-base font-bold rounded-xl shadow-button"
                  size="lg"
                  disabled={items.length === 0 || isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Pripravljam blagajno...
                    </>
                  ) : (
                    "Na blagajno"
                  )}
                </Button>

                {/* Trust indicators */}
                <div className="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    <span>Varno plačilo</span>
                  </div>
                  <div className="h-3 w-px bg-border" />
                  <div className="flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" />
                    <span>Shopify blagajna</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default CartDrawer
